import { replaceEmptyStringsWithUndefined } from 'utils/string';

import { FormType } from '../../../keys';
import { DocumentType } from '../../../types';

function isEmpty(value: any): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.values(value).every(isEmpty);
  }
  return false;
}

function removeEmptyValues<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: any = {};

  for (const [key, value] of Object.entries(obj)) {
    if (isEmpty(value)) {
      // Skip empty values
    } else if (Array.isArray(value)) {
      const cleaned = value
        .map((item) => (typeof item === 'object' && item !== null ? removeEmptyValues(item) : item))
        .filter((item) => !isEmpty(item));

      if (cleaned.length > 0) {
        result[key] = cleaned;
      }
    } else if (typeof value === 'object') {
      const cleaned = removeEmptyValues(value);
      if (!isEmpty(cleaned)) {
        result[key] = cleaned;
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}

function normalizePerson<T extends { documents?: DocumentType[]; ownershipPercentage?: unknown; firstName?: string; lastName?: string }>(
  person: T,
): T | null {
  // Skip persons without names
  if (!person.firstName || !person.lastName) return null;

  const normalized: any = { ...person };

  if (Array.isArray(normalized.documents)) {
    const validDocs = normalized.documents.filter((doc: DocumentType) => Array.isArray(doc.purposes) && doc.purposes.length > 0);
    if (validDocs.length > 0) {
      normalized.documents = validDocs;
    } else {
      delete normalized.documents;
    }
  }

  if ('ownershipPercentage' in normalized) {
    const parsed = Number(normalized.ownershipPercentage);
    normalized.ownershipPercentage = Number.isFinite(parsed) ? parsed : undefined;
  }

  return replaceEmptyStringsWithUndefined(normalized) as T;
}

export function normalizeFormValues(obj: FormType): Partial<FormType> {
  let result: any = structuredClone(obj);

  if (!result.kycScreen?.screenedAt?.trim()) {
    delete result.kycScreen;
  }
  if (!result.ofacScreen?.screenedAt?.trim()) {
    delete result.ofacScreen;
  }

  if (Array.isArray(result.documents)) {
    const validDocs = result.documents.filter((doc: DocumentType) => Array.isArray(doc.purposes) && doc.purposes.length > 0);
    result.documents = validDocs.length > 0 ? validDocs : undefined;
  }

  if (Array.isArray(result.associatedPersons)) {
    const validPersons = result.associatedPersons.map(normalizePerson).filter((person: any) => person !== null);
    result.associatedPersons = validPersons.length > 0 ? validPersons : undefined;
  }

  result = replaceEmptyStringsWithUndefined(result);

  return removeEmptyValues(result);
}
